import { inject, Injectable } from '@angular/core';
import { NbMenuItem } from '@nebular/theme';
import { Store } from '@ngrx/store';
import {
  enumEquals,
  enumIn,
  UserRole,
  UserRoleSchema,
} from '@vmspdb/definitions';
import { map } from 'rxjs';
import { SidebarNavigator } from '../contracts/sidebar-navigator';

import { selectAuthUserRole } from '@vmspdb/ui/store';

const menu: Record<string, NbMenuItem> = {
  dashboard: {
    title: 'Powerhouse',
    link: '/powerhouse',
    icon: 'home-outline',
  },

  projects: {
    title: 'Projects',
    link: '/projects',
    icon: 'folder-outline',
    pathMatch: 'prefix',
    children: [
      {
        title: 'List',
        link: '/projects',
        icon: 'archive-outline',
        pathMatch: 'full',
      },
      {
        title: 'Map',
        link: '/projects/map',
        icon: 'map',
        pathMatch: 'full',
      },
    ],
  },

  permits: {
    title: 'Permits',
    link: '/permits',
    icon: 'calendar-outline',
    pathMatch: 'prefix',
    children: [
      {
        title: 'List',
        link: '/permits',
        icon: 'archive-outline',
        pathMatch: 'full',
      },
      {
        title: 'Calendar',
        link: '/permits/calendar',
        icon: 'calendar',
        pathMatch: 'full',
      },
    ],
  },

  timeTracking: {
    title: 'Time Tracking',
    icon: 'clock-outline',
    pathMatch: 'prefix',
    children: [
      {
        title: 'My Entries',
        link: '/time-tracking',
      },
      {
        title: 'Manage',
        link: '/time-tracking/manage',
      },
      // {
      //   title: 'By Employee',
      //   link: '/time-tracking/employees',
      // },
      // {
      //   title: 'By Project',
      //   link: '/time-tracking/projects',
      // },
      // {
      //   title: 'By Crew',
      //   link: '/time-tracking/crews',
      // },
    ],
  },

  timeTrackingRestricted: {
    title: 'Time Tracking',
    icon: 'clock-outline',
    pathMatch: 'prefix',
    children: [
      {
        title: 'My Entries',
        link: '/time-tracking',
      },
    ],
  },

  surveying: {
    title: 'Surveying',
    link: '/surveying',
    icon: 'archive-outline',
    pathMatch: 'prefix',
    children: [
      {
        title: 'List',
        link: '/surveying',
        icon: 'archive-outline',
        pathMatch: 'full',
      },
      {
        title: 'Calendar',
        link: '/surveying/calendar',
        icon: 'calendar',
        pathMatch: 'full',
      },
    ],
  },

  management: {
    title: 'Management',
    expanded: true,
    icon: 'briefcase-outline',
    children: [
      // {
      //   title: 'Clients',
      //   link: '/management/clients',
      //   icon: 'briefcase-outline',
      // },
      {
        title: 'Clients',
        link: '/management/clients',
        icon: 'trending-up-outline',
        pathMatch: 'prefix',
      },
      // {
      //   title: 'Job Types',
      //   link: '/management/job-types',
      //   icon: 'layers-outline',
      //   pathMatch: 'prefix',
      // },
      {
        title: 'Employees',
        link: '/management/employees',
        icon: 'plus-square-outline',
        pathMatch: 'prefix',
      },
    ],
  },
  admin: {
    title: 'Admin',
    icon: 'settings-2-outline',
    children: [
      {
        title: 'Settings',
        icon: 'settings-outline',
        link: '/admin/settings',
        pathMatch: 'prefix',
      },
      {
        title: 'Job Types',
        link: '/admin/job-types',
        icon: 'flag-outline',
        pathMatch: 'prefix',
      },
    ],
  },
  sysop: {
    title: 'Sysop',
    icon: 'settings-2-outline',
    children: [
      {
        title: 'Subdivisions',
        link: '/management/subdivisions',
        icon: 'folder-outline',
        pathMatch: 'prefix',
      },
      {
        title: 'Users',
        icon: 'people-outline',
        link: '/admin/users',
        pathMatch: 'prefix',
      },
    ],
  },
};

@Injectable({
  providedIn: 'root',
})
export class DefaultSidebarNavigatorService implements SidebarNavigator {
  private store = inject(Store);

  get menu$() {
    return this.store
      .select(selectAuthUserRole)
      .pipe(map((role) => (role ? this.getMenu(role) : [])));
  }

  private getMenu(role: string): NbMenuItem[] {
    if (enumEquals(UserRoleSchema, role, UserRole.CLIENT)) {
      return [];
    }

    const menuItems = [menu['dashboard']];

    menuItems.push(menu['projects']);
    menuItems.push(menu['permits']);
    menuItems.push(menu['surveying']);

    if (
      enumIn(UserRoleSchema, role, [
        UserRole.SYSOP,
        UserRole.OWNER,
        UserRole.MANAGEMENT,
        UserRole.OFFICE_EMPLOYEE,
      ])
    ) {
      menuItems.push(menu['timeTracking']);
      menuItems.push(menu['management']);
    } else {
      menuItems.push(menu['timeTrackingRestricted']);
    }

    if (enumIn(UserRoleSchema, role, [UserRole.SYSOP, UserRole.OWNER])) {
      menuItems.push(menu['admin']);
    }

    if (enumEquals(UserRoleSchema, role, UserRole.SYSOP)) {
      menuItems.push(menu['sysop']);
    }

    return menuItems;
  }
}
